/**
 * Sortierbare Tabelle (V-16) - ein Baustein fuer alle Seiten (Leitlinie 3).
 * Klick auf eine Spaltenueberschrift sortiert, ein zweiter Klick dreht die
 * Richtung. Auf dem Handy scrollt die Tabelle waagerecht statt abgeschnitten
 * zu werden. Optional mit CSV-Export.
 */
import { useState, type ReactNode } from 'react';
import { ArrowDown, ArrowUp, Download } from 'lucide-react';

import { csvSpeichern, csvText, type CsvWert } from '@/lib/csv';
import { sortiere, type Richtung, type SortWert } from '@/lib/sortierung';
import { cn } from '@/lib/utils/cn';

export interface Spalte<T> {
  key: string;
  titel: string;
  /** Wert zum Sortieren und fuer die CSV-Datei. */
  wert: (z: T) => SortWert;
  /** Anzeige in der Zelle; ohne steht der Wert als Text da. */
  zelle?: (z: T) => ReactNode;
  /** Zahlen rechtsbuendig. */
  rechts?: boolean;
  /** Nicht sortierbar, z. B. eine Aktionsspalte. */
  fest?: boolean;
}

export function Tabelle<T>({ label, spalten, zeilen, zeilenKey, start, csv, leer = 'Keine Einträge.' }: {
  label: string;
  spalten: Spalte<T>[];
  zeilen: T[];
  zeilenKey: (z: T) => string;
  /** Anfangssortierung. */
  start?: { key: string; richtung: Richtung };
  /** Dateiname fuer den CSV-Export; ohne gibt es keinen Knopf. */
  csv?: string;
  leer?: string;
}) {
  const [sort, setSort] = useState(start);
  const spalte = spalten.find((s) => s.key === sort?.key);
  const liste = spalte && sort ? sortiere(zeilen, spalte.wert, sort.richtung) : zeilen;
  if (!zeilen.length) return <p className="text-[12px] text-ink-faint">{leer}</p>;

  return (
    <div className="flex flex-col gap-1.5">
      <div className="overflow-x-auto">
        <table className="w-full min-w-max text-[12px]" aria-label={label}>
          <thead className="text-left text-ink-faint">
            <tr>
              {spalten.map((s) => {
                const aktiv = sort?.key === s.key;
                return (
                  <th key={s.key} scope="col" aria-sort={aktiv ? (sort!.richtung === 'auf' ? 'ascending' : 'descending') : undefined}
                    className={cn('py-1 pr-3 font-medium last:pr-0', s.rechts && 'text-right')}>
                    {s.fest ? s.titel : (
                      <button type="button" onClick={() => setSort({ key: s.key, richtung: aktiv && sort!.richtung === 'auf' ? 'ab' : 'auf' })}
                        className={cn('inline-flex items-center gap-0.5 hover:text-ink', aktiv && 'text-ink')}>
                        {s.titel}
                        {aktiv && (sort!.richtung === 'auf' ? <ArrowUp className="size-3" /> : <ArrowDown className="size-3" />)}
                      </button>
                    )}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {liste.map((z) => (
              <tr key={zeilenKey(z)} className="border-t border-line">
                {spalten.map((s) => {
                  const v = s.wert(z);
                  return (
                    <td key={s.key} className={cn('py-1.5 pr-3 align-top last:pr-0', s.rechts && 'text-right tabular-nums')}>
                      {s.zelle ? s.zelle(z) : v === null || v === undefined || v === '' ? <span className="text-ink-faint">–</span> : typeof v === 'number' ? v.toLocaleString('de-DE', { maximumFractionDigits: 1 }) : v}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {csv && (
        <button type="button" className="inline-flex items-center gap-1 self-start text-[11px] text-ink-muted hover:text-ink"
          onClick={() => csvSpeichern(csv, csvText(spalten.filter((s) => !s.fest).map((s) => s.titel), liste.map((z) => spalten.filter((s) => !s.fest).map((s) => s.wert(z) as CsvWert))))}>
          <Download className="size-3" /> Als CSV speichern
        </button>
      )}
    </div>
  );
}
